import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MailCheck, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export function OtpVerify({ email, devMode, onVerified }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(60);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-email", { email, code });
      toast.success("Email verified!");
      onVerified(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError("");
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success("A new code has been sent.");
      setSeconds(60);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="otp-verify">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-blue-600">
        <MailCheck className="h-5 w-5" />
      </div>
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">Verify your email</h1>
      <p className="text-sm text-slate-500 mb-4">
        We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>. Enter it below to activate your account.
      </p>
      {devMode && (
        <div data-testid="otp-dev-note" className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Email delivery isn't fully configured yet — check the server logs for your code (dev mode).
        </div>
      )}
      <form onSubmit={verify} className="space-y-4">
        <Input
          data-testid="otp-input"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456"
          className="text-center tracking-[0.5em] font-pnr text-lg"
          autoFocus
        />
        {error && <div data-testid="otp-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
        <Button data-testid="otp-submit" type="submit" disabled={loading || code.length !== 6} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
        </Button>
      </form>
      <div className="mt-4 text-sm text-slate-500 flex items-center justify-between">
        <span>Didn't get it?</span>
        <Button data-testid="otp-resend" variant="ghost" size="sm" disabled={seconds > 0 || resending} onClick={resend} className="text-blue-600">
          {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="h-4 w-4 mr-1.5" /> {seconds > 0 ? `Resend in ${seconds}s` : "Resend code"}</>}
        </Button>
      </div>
    </div>
  );
}
